import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  CloudUpload,
  Download,
  Users,
  Loader2,
  Key,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDirectMailPage() {
  const [status, setStatus] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [generatingPasswords, setGeneratingPasswords] = useState(false);

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    checkStatus();
    fetchPasswordStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/directmail/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(response.data);
      
      if (response.data.connected) {
        fetchGroups();
      }
    } catch (error) {
      setStatus({ configured: false, connected: false, message: 'Fehler beim Laden' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPasswordStatus = async () => {
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/members/password-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch password status:', error);
    }
  };

  const handleGeneratePasswords = async (overwrite = false) => {
    setGeneratingPasswords(true);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/members/generate-passwords?overwrite=${overwrite}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.generated} Passwörter generiert`);
      fetchPasswordStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren');
    } finally {
      setGeneratingPasswords(false);
    }
  };

  const handleResetSync = async () => {
    if (!selectedGroup) return;
    
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/directmail/reset-sync?group_id=${selectedGroup}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setSyncResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
    }
  };

  const fetchGroups = async () => {
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/directmail/groups`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGroups(response.data.address_groups || response.data || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const handleSync = async () => {
    if (!selectedGroup) {
      toast.error('Bitte wählen Sie eine Adress-Gruppe');
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    const token = getToken();

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/directmail/sync?group_id=${selectedGroup}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSyncResult(response.data);
      toast.success(`${response.data.synced} von ${response.data.total} Mitgliedern synchronisiert`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Synchronisation fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const handleCsvExport = () => {
    const token = getToken();
    window.open(`${API_URL}/api/admin/export/members?token=${token}`, '_blank');
  };

  return (
    <Layout>
      <div className=" max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Mail className="text-primary" size={28} />
          <h1 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-foreground uppercase tracking-tight">
            DirectMail Integration
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Status Card */}
            <div className="bg-card border border-border p-6">
              <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4">
                Verbindungsstatus
              </h2>
              
              <div className="flex items-center gap-3">
                {status?.connected ? (
                  <>
                    <CheckCircle className="text-green-500" size={24} />
                    <div>
                      <p className="font-semibold text-foreground">Verbunden</p>
                      <p className="text-sm text-muted-foreground">
                        Projekt: {status.project_name || status.document_id}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <XCircle className="text-red-500" size={24} />
                    <div>
                      <p className="font-semibold text-foreground">Nicht verbunden</p>
                      <p className="text-sm text-muted-foreground">
                        {status?.message || 'Keine Verbindung zu DirectMail'}
                      </p>
                    </div>
                  </>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={checkStatus}
                  className="ml-auto rounded-none"
                >
                  <RefreshCw size={14} className="mr-2" />
                  Prüfen
                </Button>
              </div>
            </div>

            {/* Password Generation Section */}
            <div className="bg-card border border-border p-6">
              <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                <Key size={16} />
                Passwörter für DirectMail
              </h2>
              
              {/* Warning */}
              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/50 flex items-start gap-3">
                <AlertTriangle className="text-yellow-500 shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-yellow-600">Hinweis:</strong> Die generierten Passwörter werden im Klartext in der Datenbank gespeichert 
                  und können nach DirectMail synchronisiert werden. Diese Passwörter sind <strong>nicht</strong> die Login-Passwörter der Mitglieder.
                </p>
              </div>
              
              {/* Status */}
              {passwordStatus && (
                <div className="mb-4 grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-muted/30 border border-border">
                    <p className="text-2xl font-bold text-foreground">{passwordStatus.total_members}</p>
                    <p className="text-xs text-muted-foreground uppercase">Mitglieder</p>
                  </div>
                  <div className="text-center p-3 bg-green-500/10 border border-green-500/30">
                    <p className="text-2xl font-bold text-green-600">{passwordStatus.with_passwords}</p>
                    <p className="text-xs text-muted-foreground uppercase">Mit Passwort</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-500/10 border border-yellow-500/30">
                    <p className="text-2xl font-bold text-yellow-600">{passwordStatus.without_passwords}</p>
                    <p className="text-xs text-muted-foreground uppercase">Ohne Passwort</p>
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => handleGeneratePasswords(false)}
                  disabled={generatingPasswords}
                  className="bg-primary hover:bg-red-700 text-white rounded-none"
                  data-testid="generate-passwords-btn"
                >
                  {generatingPasswords ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generiere...
                    </>
                  ) : (
                    <>
                      <Key className="mr-2 h-4 w-4" />
                      Fehlende Passwörter generieren
                    </>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => handleGeneratePasswords(true)}
                  disabled={generatingPasswords}
                  className="rounded-none"
                  data-testid="regenerate-all-passwords-btn"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Alle neu generieren
                </Button>
              </div>
            </div>

            {/* Sync Section */}
            {status?.connected && (
              <div className="bg-card border border-border p-6">
                <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4">
                  Mitglieder synchronisieren
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Adress-Gruppe auswählen
                    </label>
                    <select
                      value={selectedGroup}
                      onChange={(e) => setSelectedGroup(e.target.value)}
                      className="w-full p-3 bg-background border border-border text-foreground"
                      data-testid="group-select"
                    >
                      <option value="">-- Gruppe wählen --</option>
                      {groups.map((group) => (
                        <option key={group.uuid || group.id} value={group.uuid || group.id}>
                          {group.name} ({group.address_count || 0} Adressen)
                        </option>
                      ))}
                    </select>
                  </div>

                  <Button
                    onClick={handleSync}
                    disabled={syncing || !selectedGroup}
                    className="bg-primary hover:bg-red-700 text-white rounded-none"
                    data-testid="sync-btn"
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Synchronisiere...
                      </>
                    ) : (
                      <>
                        <CloudUpload className="mr-2 h-4 w-4" />
                        Zu DirectMail synchronisieren
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleResetSync}
                    disabled={!selectedGroup}
                    className="rounded-none"
                    data-testid="reset-sync-btn"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Sync zurücksetzen
                  </Button>

                  {/* Sync Result */}
                  {syncResult && (
                    <div className={`p-4 border ${syncResult.errors?.length ? 'border-yellow-500 bg-yellow-500/10' : 'border-green-500 bg-green-500/10'}`}>
                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div className="text-center">
                          <p className="text-xl font-bold text-green-600">{syncResult.added || 0}</p>
                          <p className="text-xs text-muted-foreground uppercase">Neu hinzugefügt</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-bold text-blue-600">{syncResult.updated || 0}</p>
                          <p className="text-xs text-muted-foreground uppercase">Aktualisiert</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-bold text-gray-500">{syncResult.skipped || 0}</p>
                          <p className="text-xs text-muted-foreground uppercase">Bereits synchronisiert</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                        <Users size={16} />
                        <span>Total: {syncResult.total} Mitglieder</span>
                      </div>
                      {syncResult.without_password > 0 && (
                        <p className="text-sm text-yellow-600 mb-2">
                          <AlertTriangle className="inline mr-1" size={14} />
                          {syncResult.without_password} Mitglieder ohne Passwort
                        </p>
                      )}
                      {syncResult.errors?.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          <p className="font-medium text-yellow-600">Fehler:</p>
                          <ul className="list-disc list-inside">
                            {syncResult.errors.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Alternative Export */}
            <div className="bg-card border border-border p-6">
              <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4">
                Alternativer Export
              </h2>
              
              <p className="text-sm text-muted-foreground mb-4">
                Laden Sie eine CSV-Datei herunter, die Sie manuell in DirectMail importieren können.
              </p>
              
              <Button
                variant="outline"
                onClick={handleCsvExport}
                className="rounded-none"
                data-testid="csv-export-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                CSV für DirectMail (inkl. Passwort)
              </Button>
            </div>

            {/* Help */}
            <div className="p-4 bg-muted/30 border border-border text-sm text-muted-foreground">
              <p className="font-semibold text-foreground mb-2">Hilfe</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Generieren Sie zuerst Passwörter für alle Mitglieder</li>
                <li>Erstellen Sie eine Adress-Gruppe in DirectMail mit einem benutzerdefinierten Feld "password"</li>
                <li>Die Synchronisation überträgt: E-Mail, Vorname, Nachname und Passwort</li>
                <li>Die CSV-Datei enthält ebenfalls die Passwörter</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
